import React from 'react';
import { format, differenceInCalendarDays } from 'date-fns';
import './StatusBar.css';

const StatusBar = ({ dates }) => {
    const currentDate = new Date();

    // Calculate the total duration from the first date to the last date
    const dateKeys = Object.keys(dates);
    if (dateKeys.length === 0) {
        // No dates available. Display a red bar.
        return (
            <div className="status-container">
                <div className="status-bar error" style={{ backgroundColor: 'red' }}>
                    <span>No dates available</span>
                </div>
            </div>
        );
    }

    const firstDate = new Date(dates[dateKeys[0]]);
    const lastDate = new Date(dates[dateKeys[dateKeys.length - 1]]);

    // Check for valid date range
    if (isNaN(firstDate.getTime()) || isNaN(lastDate.getTime())) {
        return (
            <div className="status-container">
                <span className="error-message" style={{ color: 'red' }}>
                    N/A
                </span>
            </div>
        );
    }

    // Calculate the total number of days in the status bar
    const totalDays = differenceInCalendarDays(lastDate, firstDate);

    // Calculate the number of days passed since the first date
    const daysPassed = differenceInCalendarDays(currentDate, firstDate);

    // Calculate progress as a percentage
    const progressPercentage = Math.min((daysPassed / totalDays) * 100, 100); // Ensure it's capped at 100%

    // Determine the position of the marker relative to the status items
    const markerPosition = progressPercentage * 0.87; // Adjusting to fit within the status bar's height

    const getStatusClass = (date) => {
        const dateObj = new Date(date);
        return dateObj < currentDate ? 'completed' : 'pending';
    };

    return (
        <div className="status-container">
            <div className="status-bar">
                {/* Location icon as progress marker */}
                <div
                    className="progress-marker"
                    style={{
                        top: `${markerPosition}%`, // Position based on the progress percentage
                    }}
                >
                    <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="20" height="20" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="black" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className="marker-icon"
                        title={format(currentDate, 'MMM dd, yyyy')} // Tooltip showing current date
                    >
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 4.4 7 11 7 11s7-6.6 7-11c0-3.87-3.13-7-7-7z" />
                        <circle cx="12" cy="9" r="2" />
                    </svg>
                    <div className="tooltip">
                        {format(currentDate, 'MMM dd, yyyy')}
                    </div>
                </div>

                {Object.entries(dates).map(([key, date]) => {
                    const dateObj = new Date(date);

                    // Check if dateObj is valid before formatting
                    const dateFormatted = isNaN(dateObj.getTime()) ? 'N/A' : format(dateObj, 'MMM dd, yyyy');

                    return (
                        <div key={key} className="status-item">
                            <div className={`status-indicator ${getStatusClass(date)}`} />
                            <div className="status-labels">
                                <div className="status-item-label">
                                    <span className="status-label">
                                        {key.replace(/([A-Z])/g, ' $1')}
                                    </span>
                                    <span className="status-date">
                                        {dateFormatted}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default StatusBar;